import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Package,
  Plus,
  Edit3,
  Trash2,
  DollarSign,
  Tag,
  Check,
  X,
  RefreshCw,
  Search,
  Filter,
  ToggleLeft,
  ToggleRight,
  Save,
  AlertCircle,
  CheckCircle2,
  TrendingUp,
  Users,
  Layers,
  Star
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import DashboardLayout from "../../components/DashboardLayout";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Product type labels
const TYPE_LABELS = {
  plan: { label: "Plan", color: "bg-emerald-100 text-emerald-700" },
  simulation: { label: "Simulación", color: "bg-blue-100 text-blue-700" },
  human_simulation: { label: "Simulacro Humano", color: "bg-purple-100 text-purple-700" },
  foia: { label: "FOIA", color: "bg-amber-100 text-amber-700" },
  other: { label: "Otro", color: "bg-gray-100 text-gray-700" }
};

// Product Card Component
const ProductCard = ({ product, onEdit, onToggleActive }) => {
  const typeInfo = TYPE_LABELS[product.type] || TYPE_LABELS.other;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-xl border-2 ${product.active ? 'border-navy-100' : 'border-red-200 bg-red-50/30'} p-5 hover:shadow-lg transition-all`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl ${product.active ? 'bg-emerald-100' : 'bg-red-100'} flex items-center justify-center`}>
            <Package className={`w-6 h-6 ${product.active ? 'text-emerald-600' : 'text-red-500'}`} />
          </div>
          <div>
            <h3 className="font-bold text-navy-900 flex items-center gap-2">
              {product.name}
              {product.recommended && (
                <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
              )}
            </h3>
            <p className="text-xs text-navy-500 font-mono">{product.product_id}</p>
          </div>
        </div>
        <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
      </div>

      {/* Pricing */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-navy-50 rounded-lg p-3 text-center">
          <p className="text-xs text-navy-500 mb-1">Precio Vendedor</p>
          <p className="text-lg font-bold text-navy-900">${product.price}</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-3 text-center">
          <p className="text-xs text-emerald-600 mb-1">Precio Público</p>
          <p className="text-lg font-bold text-emerald-700">${product.public_price || '-'}</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-3 text-center">
          <p className="text-xs text-purple-600 mb-1">Comisión</p>
          <p className="text-lg font-bold text-purple-700">${product.commission || 0}</p>
        </div>
      </div>

      {/* Meta */}
      <div className="flex flex-wrap gap-2 mb-4">
        <span className={`px-2 py-1 rounded-full text-xs ${product.active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
          {product.active ? '✓ Activo' : '✗ Inactivo'}
        </span>
        <span className="px-2 py-1 rounded-full text-xs bg-navy-100 text-navy-600">
          Paga: {product.payer === 'vendedor' ? 'Vendedor' : product.payer === 'cliente' ? 'Cliente' : 'Ambos'}
        </span>
        {product.vendor_vip_only && (
          <span className="px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-700">
            Solo VIP
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onEdit(product)}
          className="flex-1"
        >
          <Edit3 className="w-4 h-4 mr-1" />
          Editar
        </Button>
        <Button
          size="sm"
          variant={product.active ? "outline" : "default"}
          onClick={() => onToggleActive(product)}
          className={product.active ? "text-red-600 border-red-200 hover:bg-red-50" : "bg-emerald-600 hover:bg-emerald-700"}
        >
          {product.active ? (
            <>
              <ToggleRight className="w-4 h-4 mr-1" />
              Desactivar
            </>
          ) : (
            <>
              <ToggleLeft className="w-4 h-4 mr-1" />
              Activar
            </>
          )}
        </Button>
      </div>
    </motion.div>
  );
};

// Edit Product Modal
const EditProductModal = ({ product, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: "",
    price: 0,
    public_price: 0,
    vendor_vip_only: false,
    recommended: false,
    active: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || "",
        price: product.price || 0,
        public_price: product.public_price || 0,
        vendor_vip_only: product.vendor_vip_only || false,
        recommended: product.recommended || false,
        active: product.active !== false
      });
    }
  }, [product]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/products/${product.product_id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Producto actualizado exitosamente");
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar producto");
    } finally {
      setSaving(false);
    }
  };

  const commission = (formData.public_price || 0) - (formData.price || 0);

  if (!isOpen || !product) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
      >
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-5">
          <button onClick={onClose} className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded text-white">
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Edit3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Editar Producto</h2>
              <p className="text-emerald-100 text-sm font-mono">{product.product_id}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-navy-700 mb-1">
              Nombre del producto
            </label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Plan PREMIUM"
            />
          </div>

          {/* Prices */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1">
                Precio Vendedor ($)
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
              />
              <p className="text-xs text-navy-400 mt-1">Lo que paga el vendedor</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1">
                Precio Público ($)
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.public_price}
                onChange={(e) => setFormData({ ...formData, public_price: parseFloat(e.target.value) || 0 })}
              />
              <p className="text-xs text-navy-400 mt-1">Lo que ve el cliente</p>
            </div>
          </div>

          {/* Commission Preview */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-600" />
                <span className="font-medium text-purple-700">Comisión del vendedor</span>
              </div>
              <span className="text-2xl font-bold text-purple-700">${commission.toFixed(2)}</span>
            </div>
            <p className="text-xs text-purple-600 mt-1">
              = Precio Público - Precio Vendedor
            </p>
          </div>

          {/* Toggles */}
          <div className="space-y-3">
            <label className="flex items-center justify-between p-3 bg-navy-50 rounded-lg cursor-pointer">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-navy-600" />
                <span className="text-sm text-navy-700">Solo vendedores VIP</span>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, vendor_vip_only: !formData.vendor_vip_only })}
                className={`w-12 h-6 rounded-full transition-colors ${formData.vendor_vip_only ? 'bg-amber-500' : 'bg-navy-200'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.vendor_vip_only ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </label>

            <label className="flex items-center justify-between p-3 bg-navy-50 rounded-lg cursor-pointer">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-navy-600" />
                <span className="text-sm text-navy-700">Marcar como recomendado</span>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, recommended: !formData.recommended })}
                className={`w-12 h-6 rounded-full transition-colors ${formData.recommended ? 'bg-amber-500' : 'bg-navy-200'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.recommended ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </label>

            <label className="flex items-center justify-between p-3 bg-navy-50 rounded-lg cursor-pointer">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-navy-600" />
                <span className="text-sm text-navy-700">Producto activo</span>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, active: !formData.active })}
                className={`w-12 h-6 rounded-full transition-colors ${formData.active ? 'bg-emerald-500' : 'bg-red-400'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.active ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
              {saving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Guardar Cambios
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Main Admin Products Page
const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [editingProduct, setEditingProduct] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data.products);
    } catch (error) {
      toast.error("Error al cargar productos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleEdit = (product) => {
    setEditingProduct(product);
    setShowEditModal(true);
  };

  const handleToggleActive = async (product) => {
    try {
      const token = localStorage.getItem("token");
      if (product.active) {
        await axios.delete(`${API}/products/${product.product_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Producto desactivado");
      } else {
        await axios.post(`${API}/products/${product.product_id}/activate`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Producto activado");
      }
      fetchProducts();
    } catch (error) {
      toast.error("Error al cambiar estado del producto");
    }
  };

  // Filter products
  const filteredProducts = products.filter(p => {
    const matchesFilter = filter === "all" || p.type === filter || (filter === "active" && p.active) || (filter === "inactive" && !p.active);
    const matchesSearch = !search || 
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.product_id.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Stats
  const stats = {
    total: products.length,
    active: products.filter(p => p.active).length,
    plans: products.filter(p => p.type === "plan").length,
    totalRevenue: products.filter(p => p.active && p.type === "plan").reduce((sum, p) => sum + (p.public_price || 0), 0)
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-navy-900 flex items-center gap-2">
              <Package className="w-7 h-7 text-emerald-600" />
              Gestión de Productos
            </h1>
            <p className="text-navy-600">Administra planes, precios y servicios</p>
          </div>
          <Button onClick={fetchProducts} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-navy-100">
            <div className="flex items-center gap-2 mb-2">
              <Layers className="w-5 h-5 text-navy-400" />
              <span className="text-sm text-navy-600">Total Productos</span>
            </div>
            <p className="text-2xl font-bold text-navy-900">{stats.total}</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              <span className="text-sm text-emerald-700">Activos</span>
            </div>
            <p className="text-2xl font-bold text-emerald-700">{stats.active}</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <Tag className="w-5 h-5 text-blue-500" />
              <span className="text-sm text-blue-700">Planes</span>
            </div>
            <p className="text-2xl font-bold text-blue-700">{stats.plans}</p>
          </div>
          <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-purple-500" />
              <span className="text-sm text-purple-700">Valor Catálogo</span>
            </div>
            <p className="text-2xl font-bold text-purple-700">${stats.totalRevenue.toFixed(2)}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
            <Input
              placeholder="Buscar por nombre o ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filtrar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">Activos</SelectItem>
              <SelectItem value="inactive">Inactivos</SelectItem>
              <SelectItem value="plan">Planes</SelectItem>
              <SelectItem value="simulation">Simulaciones</SelectItem>
              <SelectItem value="foia">FOIA</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-emerald-600 animate-spin" />
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-12 bg-navy-50 rounded-xl">
            <Package className="w-12 h-12 text-navy-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-navy-700 mb-2">No hay productos</h3>
            <p className="text-navy-500">No se encontraron productos con los filtros actuales</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.product_id}
                product={product}
                onEdit={handleEdit}
                onToggleActive={handleToggleActive}
              />
            ))}
          </div>
        )}

        {/* Edit Modal */}
        <EditProductModal
          product={editingProduct}
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingProduct(null);
          }}
          onSave={fetchProducts}
        />
      </div>
    </DashboardLayout>
  );
};

export default AdminProducts;
